import { chunk } from 'remeda';

import { legacyTheme, styled } from '@prose-ui/legacy';

import Carousel from 'Components/Carousel';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import useMedia from 'utils/useMedia';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import BlockLayout from './BlockLayout';
import { ProductsItem } from './ProductsItem';

const ProductsContainer = styled.div`
  width: 100%;
  overflow: hidden;

  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: ${legacyTheme.spacing.s32};

  ${legacyTheme.breakpoints.match('sm')} {
    min-height: 430px;
  }
`;

export type ProductCardType = {
  currency: string;
  slug: string;
  productPageUrl: string;
  bgLabelledPicture: string;
  bgPicture: string;
  label: string;
  isNew: boolean;
  description: string;
  capacity: string;
  priceWithSubscription: number;
  displayCTA: boolean;
  shortLabel?: string;
};

type ProductsCarouselProps = {
  background?: string;
  style?: React.CSSProperties;
  content: {
    cards: ProductCardType[];
    description?: string;
    headline: string;
  };
  dataFrom?: string;
  displayDescription?: boolean;
  margins?: string | null;
};

// @todo: remove and fix component once ProductsItem is migrated
const ProductsItemComponent = ProductsItem as any;

export const ProductsCarousel = ({
  background = 'white',
  style,
  content,
  dataFrom = 'products-block',
  displayDescription = false,
  margins,
}: ProductsCarouselProps) => {
  const { isMobile } = useResponsiveVariant();

  // This is operation is splitting each card data into groups of 3 to make 3 separate arrays
  const chunkedCards = chunk(content.cards, 3);

  return (
    // @ts-expect-error BlockLayout no migrated
    <BlockLayout
      backgroundColor={background}
      blockLayoutMargin={margins || (isMobile ? '48px auto' : '144px auto')}
      contentAlign="center"
      contentWidth="veryWide"
      style={style}
    >
      <Typography paragraph variant="h2">
        {content.headline}
      </Typography>
      {displayDescription && (
        <Typography color="grey" paragraph variant="p2">
          {content.description}
        </Typography>
      )}
      {!useMedia(`(max-width: ${breakpoints.md}px)`) ? (
        <Carousel key="desktop-product-carousel" isLooping width="900px">
          {chunkedCards.map((groupedCardsTrio) => (
            <ProductsContainer key={groupedCardsTrio.map((card) => card.slug).join('-')}>
              {groupedCardsTrio.map(
                ({
                  currency,
                  slug,
                  productPageUrl,
                  bgLabelledPicture,
                  bgPicture,
                  label,
                  isNew,
                  description,
                  capacity,
                  priceWithSubscription,
                  displayCTA,
                  shortLabel = label,
                }) => (
                  <ProductsItemComponent
                    key={slug}
                    bgLabelledPicture={bgLabelledPicture}
                    bgPicture={bgPicture}
                    capacity={capacity}
                    currency={currency}
                    dataFrom={dataFrom}
                    description={description}
                    displayCTA={displayCTA}
                    isNew={isNew}
                    label={shortLabel}
                    price={priceWithSubscription}
                    productPageUrl={productPageUrl}
                    slug={slug}
                  />
                ),
              )}
            </ProductsContainer>
          ))}
        </Carousel>
      ) : (
        <Carousel key="mobile-product-carousel" isInfinite isLooping width="300px">
          {content.cards.map(
            ({
              currency,
              slug,
              productPageUrl,
              bgLabelledPicture,
              bgPicture,
              label,
              isNew,
              description,
              capacity,
              priceWithSubscription,
              displayCTA,
              shortLabel = label,
            }) => (
              <ProductsContainer key={slug}>
                <ProductsItemComponent
                  bgLabelledPicture={bgLabelledPicture}
                  bgPicture={bgPicture}
                  capacity={capacity}
                  currency={currency}
                  dataFrom={dataFrom}
                  description={description}
                  displayCTA={displayCTA}
                  isNew={isNew}
                  label={shortLabel}
                  price={priceWithSubscription}
                  productPageUrl={productPageUrl}
                  slug={slug}
                />
              </ProductsContainer>
            ),
          )}
        </Carousel>
      )}
    </BlockLayout>
  );
};
